import { stringify as qsStringify } from 'qs';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { MESSAGES } from '../CommonConstants';
import fetchWithDefaults from '../CommonUtils/fetchWithDefaults';

const GENERIC_ERRORS = [MESSAGES.GENERIC_ERROR];

type FetchFunction = (fetchOptions?: {
  body?: any;
  queryParams?: Record<string, string | number>;
  onSuccess?: (payload: any, errors?: string[]) => void;
  onError?: (errors: string[]) => void;
  onFinish?: () => void;
}) => void;

type UseApiHook = (
  path: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    reduxActionPrefix?: string;
    bodyEncoding?: 'jsonParam' | 'formData' | 'json';
  },
) => [
  FetchFunction,
  { loading: boolean; errors: string[]; data: any },
  {
    setErrors(errors: string[]): void;
  },
];

const useApi: UseApiHook = (path, options) => {
  const { method = 'GET', reduxActionPrefix, bodyEncoding = 'jsonParam' } = options || {
    method: 'GET',
    bodyEncoding: 'jsonParam',
  };

  const dispatch = reduxActionPrefix ? useDispatch() : () => {};

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [data, setData] = useState(null);

  const fetchFunc: FetchFunction = async fetchOptions => {
    const {
      body: rawBody,
      queryParams = {},
      onSuccess = () => {},
      onError = () => {},
      onFinish = () => {},
    } = fetchOptions || {
      body: undefined,
      queryParams: {},
      onSuccess: () => {},
      onError: () => {},
      onFinish: () => {},
    };

    try {
      setLoading(true);
      setErrors([]);

      dispatch({ type: `${reduxActionPrefix}_START` });

      let body = rawBody;

      if (body) {
        switch (bodyEncoding) {
          case 'jsonParam':
            body = new FormData();

            body.append('json', JSON.stringify(rawBody));

            break;
          case 'formData':
            body = new FormData();

            Object.entries(rawBody).forEach(([k, v]) => body.append(k, v));

            break;
          case 'json':
            body = JSON.stringify(rawBody);
            break;
          default:
        }
      }

      const response = await fetchWithDefaults(
        `${path}${qsStringify(queryParams, { addQueryPrefix: true })}`,
        { method, body },
      );

      if (response.ok) {
        const {
          success,
          errors: apiErrors,
          payload: { viewData },
        }: { success: boolean; errors: string[]; payload: any } = await response.json();

        if (success) {
          await dispatch({ type: `${reduxActionPrefix}_SUCCESS`, payload: viewData });

          setData(viewData);

          setLoading(false);

          setErrors([]);

          onSuccess(viewData, apiErrors);
        } else {
          dispatch({ type: `${reduxActionPrefix}_ERROR`, payload: errors });

          setData(null);

          setLoading(false);

          setErrors(apiErrors);

          onError(apiErrors);
        }
      } else {
        dispatch({ type: `${reduxActionPrefix}_ERROR`, payload: GENERIC_ERRORS });

        setData(null);

        setLoading(false);

        setErrors(GENERIC_ERRORS);

        onError(GENERIC_ERRORS);
      }
    } catch (err) {
      dispatch({ type: `${reduxActionPrefix}_ERROR`, payload: GENERIC_ERRORS });

      setData(null);

      setLoading(false);

      setErrors(GENERIC_ERRORS);

      onError(GENERIC_ERRORS);

      console.warn(err);
    } finally {
      setLoading(false);

      onFinish();
    }
  };

  return [fetchFunc, { loading, errors, data }, { setErrors }];
};

export default useApi;
